import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { openBrandedPrintWindow } from "@/lib/documentTemplate";
import { ShoppingCart, Printer, UserPlus, ChevronDown, ChevronRight, Package2, FileDown, Truck, PackageCheck, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";

const statusColor = (s) => ({
  draft: "bg-muted text-muted-foreground border-border",
  issued: "bg-primary/15 text-primary border-primary/30",
  acknowledged: "bg-sky-500/15 text-sky-500 border-sky-500/30",
  partially_received: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  received: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  cancelled: "bg-rose-500/15 text-rose-500 border-rose-500/30",
}[s] || "bg-muted text-muted-foreground border-border");

const fmtInr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function PurchaseOrders() {
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const [grnFor, setGrnFor] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (statusFilter) p.append("status", statusFilter);
      if (vendorFilter) p.append("vendor_id", vendorFilter);
      const [pos, v] = await Promise.all([
        api.get(`/purchase-orders?${p.toString()}`),
        api.get("/vendors"),
      ]);
      setRows(pos.data); setVendors(v.data);
    } catch (e) { toast.error("Failed to load POs"); }
    finally { setLoading(false); }
  }, [statusFilter, vendorFilter]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (po, status) => {
    try { await api.patch(`/purchase-orders/${po.id}/status`, { status }); toast.success(`PO ${po.po_number} · ${status.replace("_"," ")}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const grouped = useMemo(() => {
    const m = new Map();
    for (const po of rows) {
      const key = po.vendor_id || "__unassigned__";
      const bucket = m.get(key) || { vendor_name: po.vendor_name, vendor_id: po.vendor_id, pos: [], total: 0 };
      bucket.pos.push(po);
      bucket.total += Number(po.total || 0);
      m.set(key, bucket);
    }
    return Array.from(m.values()).sort((a, b) => b.total - a.total);
  }, [rows]);

  const printPO = (po) => {
    const body = `
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:12px">
        <div>
          <div style="font-size:9px;letter-spacing:1.5px;text-transform:uppercase;color:#73706A">Supplier</div>
          <div style="font-size:16px;font-weight:600;margin-top:2px">${po.vendor_name || "—"}</div>
          <div style="font-size:11px;color:#73706A;margin-top:4px;line-height:1.6">
            ${po.vendor_snapshot?.company ? po.vendor_snapshot.company + "<br>" : ""}
            ${po.vendor_snapshot?.gst ? "GST: " + po.vendor_snapshot.gst + "<br>" : ""}
            ${po.vendor_snapshot?.phone ? po.vendor_snapshot.phone + "<br>" : ""}
            ${po.vendor_snapshot?.email || ""}
          </div>
        </div>
        <div style="text-align:right;font-size:11px;color:#73706A">
          <div>Ref BOQ: <b style="color:#1A1918">${po.boq_number || po.boq_id || "—"}</b></div>
          <div>Payment: <b style="color:#1A1918">${po.vendor_snapshot?.payment_terms || "—"}</b></div>
          <div>Status: <b style="color:#1A1918;text-transform:uppercase">${(po.status || "").replace("_"," ")}</b></div>
        </div>
      </div>
      <table><thead><tr><th style="width:80px">Code</th><th>Description</th><th style="width:60px">Unit</th><th class="right" style="width:60px">Qty</th><th class="right" style="width:90px">Rate</th><th class="right" style="width:110px">Amount</th></tr></thead>
      <tbody>${po.items.map(i => `<tr><td style="font-family:monospace;font-size:10px">${i.item_code || "—"}</td><td>${i.description || ""}</td><td>${i.unit || ""}</td><td class="right">${i.quantity}</td><td class="right">${fmtInr(i.rate)}</td><td class="right">${fmtInr(i.amount)}</td></tr>`).join("")}</tbody></table>
      <div style="margin-top:14px;margin-left:auto;width:280px;font-size:12px">
        <div style="display:flex;justify-content:space-between;padding:5px 0"><span>Subtotal</span><span>${fmtInr(po.subtotal)}</span></div>
        <div style="display:flex;justify-content:space-between;padding:5px 0;color:#73706A"><span>GST (${po.gst_percent || 0}%)</span><span>${fmtInr(po.tax)}</span></div>
        <div style="display:flex;justify-content:space-between;padding:10px 0;margin-top:6px;border-top:2px solid #1A1918;font-weight:700;font-size:14px"><span>TOTAL</span><span>${fmtInr(po.total)}</span></div>
      </div>
      ${po.notes ? `<div style="margin-top:20px;padding:12px;background:#F7F5F0;border-radius:6px;font-size:11px"><b style="text-transform:uppercase;font-size:9px;letter-spacing:1.5px;color:#73706A">Notes</b><div style="margin-top:6px">${po.notes.replace(/\n/g, "<br>")}</div></div>` : ""}
    `;
    openBrandedPrintWindow(body, {
      subject: `PO ${po.po_number}`,
      title: "Purchase Order",
      number: po.po_number,
      date: new Date(po.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" }),
    });
  };

  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1 flex items-center gap-2"><ShoppingCart className="w-3.5 h-3.5" /> Procurement</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold">Purchase Orders</h1>
          <p className="text-sm text-muted-foreground mt-2">Grouped by supplier · every PO on this page was generated one-click from a BOQ.</p>
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          <Select value={statusFilter || "__all__"} onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-40"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              {["draft","issued","acknowledged","partially_received","received","cancelled"].map(s => <SelectItem key={s} value={s}>{s.replace(/_/g," ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={vendorFilter || "__all__"} onValueChange={(v) => setVendorFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-56"><SelectValue placeholder="All vendors" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All vendors</SelectItem>
              {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && <div className="text-center py-12 text-muted-foreground">Loading…</div>}
      {!loading && grouped.length === 0 && <div className="text-center py-16 rounded-2xl border border-dashed border-border/60 text-muted-foreground">No purchase orders yet. Open a BOQ and hit "Convert to PO".</div>}

      {grouped.map(g => (
        <div key={g.vendor_id || "__u__"} className="rounded-2xl bg-card border border-border/60 overflow-hidden" data-testid={`po-group-${g.vendor_id || "unassigned"}`}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 bg-muted/20">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full grid place-items-center text-[10px] font-bold ${g.vendor_id ? "bg-primary/15 text-primary" : "bg-amber-500/15 text-amber-600"}`}>{(g.vendor_name || "?").slice(0,1).toUpperCase()}</div>
              <div>
                <div className="text-sm font-semibold">{g.vendor_name || "Unassigned"}</div>
                <div className="text-xs text-muted-foreground">{g.pos.length} PO{g.pos.length === 1 ? "" : "s"} · Total {fmtInr(g.total)}</div>
              </div>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              <tr><th className="text-left py-2 px-4">PO Number</th><th className="text-left">BOQ</th><th className="text-left">Items</th><th className="text-right">Subtotal</th><th className="text-right">Tax</th><th className="text-right pr-2">Total</th><th>Status</th><th className="pr-4 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {g.pos.map(po => (
                <tr key={po.id} data-testid={`po-row-${po.id}`} className="border-t border-border/50 hover:bg-muted/20">
                  <td className="py-2.5 px-4 font-mono text-[11px]">{po.po_number}</td>
                  <td className="text-xs text-muted-foreground">{po.boq_number || "—"}</td>
                  <td className="text-xs text-muted-foreground">{po.items.length}</td>
                  <td className="text-right">{fmtInr(po.subtotal)}</td>
                  <td className="text-right text-muted-foreground">{fmtInr(po.tax)}</td>
                  <td className="text-right pr-2 font-semibold">{fmtInr(po.total)}</td>
                  <td>
                    <Select value={po.status} onValueChange={(v) => updateStatus(po, v)}>
                      <SelectTrigger className={`h-7 w-40 text-[11px] ${statusColor(po.status)}`} data-testid={`po-status-${po.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["draft","issued","acknowledged","partially_received","received","cancelled"].map(s => <SelectItem key={s} value={s} className="capitalize text-xs">{s.replace(/_/g," ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="pr-4">
                    <div className="flex items-center justify-end gap-1">
                      {!po.vendor_id && (
                        <button onClick={() => setAssignFor(po)} data-testid={`po-assign-${po.id}`} className="p-1.5 rounded hover:bg-muted text-amber-600" title="Assign vendor"><UserPlus className="w-3.5 h-3.5" /></button>
                      )}
                      {po.vendor_id && po.status !== "cancelled" && po.status !== "received" && (
                        <button onClick={() => setGrnFor(po)} data-testid={`po-grn-${po.id}`} className="p-1.5 rounded hover:bg-muted text-emerald-600" title="Record goods received"><Truck className="w-3.5 h-3.5" /></button>
                      )}
                      {po.status === "received" && (
                        <span className="p-1.5 text-emerald-600" title="Fully received"><PackageCheck className="w-3.5 h-3.5" /></span>
                      )}
                      <button onClick={() => setOpenId(openId === po.id ? null : po.id)} data-testid={`po-details-${po.id}`} className="p-1.5 rounded hover:bg-muted" title="Details">{openId === po.id ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}</button>
                      <button onClick={() => printPO(po)} data-testid={`po-print-${po.id}`} className="p-1.5 rounded hover:bg-muted text-primary" title="Print PO"><Printer className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {g.pos.map(po => openId === po.id && (
                <tr key={`d-${po.id}`}><td colSpan={8} className="bg-muted/10 border-t border-border/50 px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Line items ({po.items.length})</div>
                  <table className="w-full text-xs">
                    <thead className="text-[9px] uppercase tracking-widest text-muted-foreground">
                      <tr className="border-b border-border/50"><th className="text-left py-1 w-20">Code</th><th className="text-left">Description</th><th>Unit</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">Amount</th></tr>
                    </thead>
                    <tbody>
                      {po.items.map((it, i) => (
                        <tr key={i} className="border-b border-border/50 last:border-0"><td className="py-1 font-mono">{it.item_code || "—"}</td><td>{it.description}</td><td className="text-muted-foreground">{it.unit}</td><td className="text-right">{it.quantity}</td><td className="text-right">{fmtInr(it.rate)}</td><td className="text-right font-medium">{fmtInr(it.amount)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </td></tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {/* Assign vendor dialog */}
      <AssignVendorDialog open={!!assignFor} po={assignFor} vendors={vendors} onClose={() => setAssignFor(null)} onSaved={() => { setAssignFor(null); load(); }} />
      <GrnDialog open={!!grnFor} po={grnFor} onClose={() => setGrnFor(null)} onSaved={() => { setGrnFor(null); load(); }} />
    </div>
  );
}

function AssignVendorDialog({ open, po, vendors, onClose, onSaved }) {
  const [vid, setVid] = useState("");
  useEffect(() => { setVid(""); }, [open]);
  const save = async () => {
    if (!vid) return toast.error("Pick a vendor");
    try { await api.patch(`/purchase-orders/${po.id}/assign-vendor`, { vendor_id: vid }); toast.success("Vendor assigned · PO issued"); onSaved && onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Assign Vendor · {po?.po_number}</DialogTitle>
          <DialogDescription>Pick a vendor for this unassigned PO. Status will move to "Issued" once assigned.</DialogDescription>
        </DialogHeader>
        <Select value={vid} onValueChange={setVid}>
          <SelectTrigger data-testid="assign-vendor-select"><SelectValue placeholder="Choose vendor…" /></SelectTrigger>
          <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}{v.company ? ` · ${v.company}` : ""}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={save} data-testid="assign-vendor-save" className="bg-primary text-primary-foreground">Assign</Button>
      </DialogContent>
    </Dialog>
  );
}

// -------------- GRN (Goods Received) Dialog --------------
function GrnDialog({ open, po, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [existing, setExisting] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dn, setDn] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open || !po) return;
    setDn(""); setNotes(""); setLoading(true);
    Promise.all([
      api.get(`/purchase-orders/${po.id}/receipt-status`),
      api.get(`/purchase-orders/${po.id}/grns`),
    ])
      .then(([rs, gs]) => {
        setRows(rs.data.lines.map((l) => ({ ...l, receive_now: l.pending_qty, remarks: "" })));
        setExisting(gs.data);
      })
      .catch(() => { setRows([]); setExisting([]); })
      .finally(() => setLoading(false));
  }, [open, po]);

  const totalNow = rows.reduce((s, r) => s + Number(r.receive_now || 0), 0);
  const anyToReceive = rows.some((r) => Number(r.receive_now || 0) > 0);

  const save = async () => {
    const lines = rows
      .filter((r) => Number(r.receive_now || 0) > 0)
      .map((r) => ({ item_code: r.item_code, description: r.description, unit: r.unit, received_qty: Number(r.receive_now), remarks: r.remarks }));
    if (!lines.length) return toast.error("Enter at least one non-zero receipt quantity");
    setSaving(true);
    try {
      const { data } = await api.post(`/purchase-orders/${po.id}/grns`, { lines, delivery_note_no: dn, notes });
      toast.success(`${data.grn.grn_number} recorded · PO now ${data.po.status.replace(/_/g, " ")}`);
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Truck className="w-4 h-4 text-emerald-500" /> Goods Received · {po?.po_number}</DialogTitle>
          <DialogDescription>Enter today's received quantity per line. Inventory is auto-incremented for materials matched by SKU · PO status auto-updates to <b>Partially Received</b> or <b>Received</b>.</DialogDescription>
        </DialogHeader>
        {loading ? <div className="py-8 text-center text-muted-foreground flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading receipts…</div> : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-muted-foreground uppercase tracking-widest">Delivery note #</label><Input value={dn} onChange={(e) => setDn(e.target.value)} placeholder="DN-XXXX" data-testid="grn-dn" /></div>
              <div><label className="text-xs text-muted-foreground uppercase tracking-widest">Notes (optional)</label><Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any remarks" data-testid="grn-notes" /></div>
            </div>

            <div className="border border-border/60 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  <tr>
                    <th className="text-left py-2 px-3">Item</th>
                    <th className="text-right w-20">Ordered</th>
                    <th className="text-right w-20">Received</th>
                    <th className="text-right w-20">Pending</th>
                    <th className="text-right w-28">Receive now</th>
                    <th className="text-left w-56 pl-2">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} data-testid={`grn-line-${r.item_code || i}`} className="border-t border-border/50">
                      <td className="py-2 px-3">
                        <div className="font-medium">{r.description}</div>
                        <div className="text-[11px] text-muted-foreground font-mono">{r.item_code || "—"} · {r.unit}</div>
                      </td>
                      <td className="text-right">{r.ordered_qty}</td>
                      <td className="text-right text-muted-foreground">{r.received_qty}</td>
                      <td className={`text-right font-medium ${r.pending_qty > 0 ? "text-amber-600" : "text-emerald-500"}`}>{r.pending_qty}</td>
                      <td className="text-right">
                        <Input type="number" min="0" max={r.pending_qty} step="0.01" value={r.receive_now}
                          onChange={(e) => { const v = e.target.value; setRows((rs) => rs.map((x, idx) => idx === i ? { ...x, receive_now: v } : x)); }}
                          disabled={r.pending_qty <= 0}
                          data-testid={`grn-qty-${r.item_code || i}`}
                          className="h-8 text-right w-24 ml-auto" />
                      </td>
                      <td className="pl-2">
                        <Input value={r.remarks} onChange={(e) => setRows((rs) => rs.map((x, idx) => idx === i ? { ...x, remarks: e.target.value } : x))} placeholder="—" className="h-8 text-xs" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {existing.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground py-1">{existing.length} previous GRN{existing.length === 1 ? "" : "s"} on this PO</summary>
                <div className="mt-2 space-y-1 pl-3">
                  {existing.map((g) => (
                    <div key={g.id} className="flex items-center justify-between border-l-2 border-border/60 pl-3 py-1">
                      <div>
                        <div className="font-medium">{g.grn_number}{g.delivery_note_no ? ` · DN ${g.delivery_note_no}` : ""}</div>
                        <div className="text-muted-foreground">{new Date(g.received_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} · {g.received_by_name || "—"}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono">{g.total_received_qty} unit{g.total_received_qty === 1 ? "" : "s"}</div>
                        <div className="text-muted-foreground">₹{Number(g.total_value || 0).toLocaleString("en-IN")}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}

            <div className="flex items-center justify-between border-t border-border/60 pt-3">
              <div className="text-xs text-muted-foreground">Total this receipt: <b className="text-foreground">{totalNow}</b> units</div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                <Button onClick={save} disabled={saving || !anyToReceive} data-testid="grn-save-btn" className="bg-primary text-primary-foreground">
                  {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Recording…</> : <><PackageCheck className="w-4 h-4 mr-1.5" />Record Receipt</>}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
